import React, { useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { API_HOST } from '@/core/variables';
import { toSpliced, withArray } from '@/util/arrayUtil';
import { useIntegration, useUpsertKakaoTalk } from '@/query/integration';
import { useScenarioId } from '@/hook';
import Button from '@/component/Button';
import Tooltip from '@/component/Tooltip';
import { NumberInput, SwitchInput } from '@/component/input';
import { errorToast, infoToast, successToast } from '@/component/Toast';
import { AddIcon, DeleteIcon, FoldedDocumentIcon, PasteIcon } from '@/assets/svgs/icons';


const KakaotalkIntegrationView: React.FC = () => {
    const intl = useIntl();
    const scenarioId = useScenarioId();

    const panelId = useId();
    const linkUrlInputRef = useRef<HTMLInputElement>(null);
    const pendingTimeoutInputRef = useRef<HTMLInputElement>(null);
    const [pendingMessages, setPendingMessages] = useState<string[]>([]);
    const [enabled, setEnabled] = useState<boolean>(false);
    const [enableUnhtml, setEnableUnhtml] = useState<boolean>(false);

    const { data: publishData } = useIntegration(scenarioId);
    const { mutateAsync: upsertKakaoTalk, isPending: isSaving } = useUpsertKakaoTalk(scenarioId);

    const kakaoTalkIntegration = publishData?.integrations?.kakaotalk;

    const onClickMenual = () => {
        infoToast(intl.formatMessage({ id: "i08012" }));
    };

    const onSave = async () => {
        await upsertKakaoTalk({
            enabled: enabled,
            enable_unhtml: enableUnhtml,
            link_url: linkUrlInputRef.current?.value ?? "",
            pending_timeout: pendingTimeoutInputRef.current?.value ?? 0,
            pending_messages: pendingMessages,
        }).then(() => {
            successToast(intl.formatMessage({ id: "t003" }));
        })
        .catch(() => {
            errorToast(intl.formatMessage({ id: "t004" }));
        });
    };

    useEffect(() => {
        setEnabled(kakaoTalkIntegration?.enabled ?? false);
        setEnableUnhtml(kakaoTalkIntegration?.enable_unhtml ?? false);
        setPendingMessages(kakaoTalkIntegration?.pending_messages ?? []);
    }, [kakaoTalkIntegration]);

    if (!publishData) {
        return null;
    }
    return (
        <main className="scenario-content-layout">
            <div className="w-sm-page flex flex-col">
                <div className="flex justify-between items-center">
                    <div className="text-[16px] font-[600] text-black-500">
                        {intl.formatMessage({ id: "b0043" })}
                    </div>

                    <Button icon
                            variant="text"
                            onClick={onClickMenual}
                    >
                        <FoldedDocumentIcon className="size-3.5"
                                            aria-hidden="true"
                        />
                        {intl.formatMessage({ id: "i08111" })}
                    </Button>
                </div>

                <div className="integration-card mt-4">
                    <div className="w-full flex justify-between items-center">
                        <label htmlFor="kakaotalk-enable"
                               className="text-[14px] font-[600] text-black-500"
                        >
                            {intl.formatMessage({ id: "i08101" })}
                        </label>
                        <SwitchInput id="kakaotalk-enable"
                                     value={enabled}
                                     aria-expanded={enabled}
                                     aria-controls={panelId}
                                     onSwitch={setEnabled}
                        />
                    </div>
                    <div id={panelId}
                         role="region"
                         className={classNames(
                             "integration-card-body",
                             "transition-disclosure",
                             enabled ? "open" : "closed"
                         )}
                         aria-hidden={!enabled}
                    >
                        <div className="integration-card-body-section">
                            <div className="integration-card-input-group">
                                <div className="integration-card-label">
                                    {intl.formatMessage({ id: "i08103" })}
                                </div>
                                <CopyableUrl url={`${API_HOST}/integration/${scenarioId}/kakaotalk/welcome`} />
                            </div>

                            <div className="integration-card-input-group">
                                <div className="integration-card-label">
                                    {intl.formatMessage({ id: "i08104" })}
                                </div>
                                <CopyableUrl url={`${API_HOST}/integration/${scenarioId}/kakaotalk/message`} />
                            </div>

                            <div className="integration-card-input-group">
                                <label htmlFor="kakaotalk-link-url"
                                       className="integration-card-label"
                                >
                                    {intl.formatMessage({ id: "i08105" })}
                                </label>
                                <input type="text"
                                       id="kakaotalk-link-url"
                                       ref={linkUrlInputRef}
                                       className="input-default"
                                       defaultValue={kakaoTalkIntegration?.link_url ?? ""}
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <label htmlFor="kakaotalk-unhtml"
                                       className="integration-card-label"
                                >
                                    {intl.formatMessage({ id: "i08106" })}
                                </label>
                                <SwitchInput id="kakaotalk-unhtml"
                                             value={enableUnhtml}
                                             onSwitch={setEnableUnhtml}
                                />
                            </div>
                        </div>

                        <div className="integration-card-body-section">
                            <div className="text-[14px] font-[600] text-black-500">
                                {intl.formatMessage({ id: "i08107" })}
                            </div>
                            <div className="integration-card-input-group">
                                <div className="integration-card-label">
                                    {intl.formatMessage({ id: "i08104" })}
                                </div>
                                <CopyableUrl url={`${API_HOST}/integration/${scenarioId}/kakaotalk/message/v2`} />
                            </div>

                            <div className="integration-card-input-group">
                                <label htmlFor="kakaotalk-pending-timeout"
                                       className="integration-card-label"
                                >
                                    {intl.formatMessage({ id: "i08108" })}
                                </label>
                                <NumberInput id="kakaotalk-pending-timeout"
                                             width="100%"
                                             min={0} max={30}
                                             ref={pendingTimeoutInputRef}
                                             defaultValue={kakaoTalkIntegration?.pending_timeout}
                                />
                            </div>

                            <div className="integration-card-input-group">
                                <div className="flex gap-1 items-center">
                                    <div className="integration-card-label">
                                        {intl.formatMessage({ id: "i08109" })}
                                    </div>
                                    <Tooltip message={intl.formatMessage({ id: "i08110" })} />
                                    <Button variant="text-transparent"
                                            aria-label={intl.formatMessage({ id: "b0009" })}
                                            onClick={() => setPendingMessages([...pendingMessages, ""])}
                                    >
                                        <AddIcon className="size-3.5"
                                                 aria-hidden="true"
                                        />
                                    </Button>
                                </div>
                                <div className="flex flex-col gap-2">
                                    {pendingMessages.map((message, index) => (
                                        <div key={index} className="flex gap-4 items-center">
                                            <input type="text"
                                                   className="input-default"
                                                   aria-label={`${intl.formatMessage({ id: "i08109" })} ${index + 1}`}
                                                   value={message}
                                                   placeholder={intl.formatMessage({ id: "i08102" })}
                                                   onChange={e => setPendingMessages(withArray(pendingMessages, index, e.target.value))}
                                            />
                                            <Button variant="text-transparent"
                                                    aria-label={intl.formatMessage({ id: "b0006" })}
                                                    onClick={() => setPendingMessages(toSpliced(pendingMessages, index, 1))}
                                            >
                                                <DeleteIcon className="size-4 text-point-red"
                                                            aria-hidden="true"
                                                />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Button variant="primary" size="sm"
                        className="ml-auto mt-3"
                        disabled={isSaving}
                        onClick={onSave}
                >
                    {intl.formatMessage({ id: 'b0007' })}
                </Button>
            </div>
        </main>
    );
};


const CopyableUrl: React.FC<{url: string}> = ({ url }) => {
    const intl = useIntl();
    const urlId = useId();

    const onClickCopy = () => {
        navigator.clipboard.writeText(url).then(() => {
            successToast(intl.formatMessage({ id: "i08011" }));
        });
    };

    return (
        <div className="flex items-center gap-3">
            <div id={urlId}
                 className="w-full py-2 px-3.5 rounded-[6px] bg-black-200 text-[14px] font-[400] text-black-500"
            >
                {url}
            </div>
            <Button aria-label={intl.formatMessage({ id: "b0062" })}
                    aria-describedby={urlId}
                    onClick={onClickCopy}
            >
                <PasteIcon className="size-4"
                           aria-hidden="true"
                />
            </Button>
        </div>
    );
};

export default KakaotalkIntegrationView;
